import React, { useEffect, useMemo, useState } from 'react';
import { useAPI } from 'utils/api';
import { UrlParam } from 'types/Misc';
import { hex2rgba } from 'utils/hexToRgba';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { shadeChanger } from 'utils/shadeChanger';
import { useGlobal } from 'context/global/GlobalContext';
import { showToast } from 'components/atomic/Toast/Toast';
import { useParams, useLocation } from 'react-router-dom';
import { CookiesConsentType } from 'types/CookiesConsentType';
import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { useCookieConsent } from 'context/CookieConsentContext/CookieConsentContext';
import { ReactComponent as Chevron } from 'assets/icons/svg/arrow-down-color.svg';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import cookieCheckbox from 'assets/icons/svg/cookie-checkbox.svg';
import HtmlWrapper from 'components/wrappers/HtmlWrapper';
import PageWrapper from 'components/layout/PageWrapper';
import useRedirectLink from 'hooks/useRedirectLink';
import Button from 'components/atomic/Button';
import SimpleBar from 'simplebar-react';
import Cookies from 'js-cookie';
import { ExperienceData } from 'utils/linkTracking';

type CookiePreferences = {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  social: boolean;
};

interface UserUpdatePayload {
  brandId?: string;
  cookieConsent?: {
    consentType: string;
    allowed: boolean;
    consentFor: string;
    language: string;
    defaultLanguage: string;
    meta?: any;
  };
}

const TermsAndPrivacy: React.FC = () => {
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(
    {} as CookiesConsentType
  );
  const [cookieDropdownOpen, setCookieDropdownOpen] = useState<boolean>(false);
  const { id } = useParams<UrlParam>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'termsAndPrivacy',
  });
  const { t: cookieBanner } = useTranslation('translation', {
    keyPrefix: 'cookieBanner',
  });
  const {
    user,
    setSlug,
    termsLoading,
    productDetails,
    termsAndPrivacyDetails,
  } = useGlobal();
  const { brandTheme, buttonStyle } = useThemeContext();
  const { setCookiesConsent } = useCookieConsent();
  const redirectToExternalLink = useRedirectLink();
  const location = useLocation();

  useEffect(() => {
    if (id) setSlug(id);
  }, [id, setSlug]);

  useEffect(() => {
    const storedCookiePreferences = Cookies.get(`cookieConsent`) || '';
    if (storedCookiePreferences) {
      try {
        const parsedPreferences = JSON.parse(storedCookiePreferences);
        setCookiePreferences(parsedPreferences);
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
      }
    }
  }, []);

  const handleCheckboxChange = (type: keyof CookiesConsentType) => {
    setCookiePreferences((prev: any) => ({ ...prev, [type]: !prev[type] }));
  };

  const [updateUser, loading] = useAPI<UserUpdatePayload>({
    method: 'PUT',
    endpoint: 'auth/update',
    onSuccess: () =>
      showToast({ message: cookieBanner('preferencesSaved'), type: 'success' }),
    onError: () =>
      showToast({ message: cookieBanner('cookieSaveError'), type: 'error' }),
  });

  const updateGTMConsent = (consent: CookiesConsentType) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'default_consent',
      consent: {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data:
          consent.marketing && consent.analytics ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
      },
    });
  };

  const cookieEventTrigger = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'brij-cookies-initiation',
    });
  };

  const handleAcceptSelected = () => {
    const newCookiesConsent = {
      ...cookiePreferences,
    };
    setCookiesConsent(newCookiesConsent);
    Cookies.set('cookieConsent', JSON.stringify(newCookiesConsent), {
      expires: 365,
    });
    updateGTMConsent(newCookiesConsent);
    cookieEventTrigger();
    if (!user)
      showToast({
        message: cookieBanner('preferencesSaved'),
        type: 'success',
      });
    if (user)
      updateUser({
        brandId: productDetails?.brand?.id,
        cookieConsent: {
          allowed: newCookiesConsent?.essential || true,
          consentType: 'explicit',
          consentFor: 'cookies',
          language:
            productDetails?.brand?.cookieConsentSettings?.description || '',
          defaultLanguage: '',
          meta: newCookiesConsent,
        },
      });
  };

  const getCornerStyle = () => {
    switch (buttonStyle) {
      case CornerStyle.FULL_ROUND:
        return 'rounded-[26px]';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-sm1';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return 'rounded-[26px]';
    }
  };

  const getAriaLabel = (key: keyof CookiesConsentType) => {
    switch (key) {
      case 'essential':
        return cookieBanner('essentialCookiesDescription');
      case 'functional':
        return cookieBanner('functionalCookiesDescription');
      case 'analytics':
        return cookieBanner('analyticsCookiesDescription');
      case 'marketing':
        return cookieBanner('marketingCookiesDescription');
      case 'social':
        return cookieBanner('socialCookiesDescription');
      default:
        return '';
    }
  };

  const experienceData: ExperienceData | null = useMemo(() => {
    if (!productDetails?.product) return null;
    return {
      id:
        productDetails.product?.experienceDetails?.experienceBrijID ||
        productDetails.product?.id ||
        '',
      name: productDetails.product?.name || '',
      title: productDetails.product?.displayName || '',
    };
  }, [productDetails?.product]);

  return (
    <PageWrapper
      pageTitle={t('pageHeaderTitle')}
      allowGoBack={productDetails ? true : false}
    >
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>
      {termsLoading ? (
        <div className='relative w-full h-full flex items-center justify-center pb-16'>
          <LoadingIndicator />
        </div>
      ) : (
        <SimpleBar autoHide={false} autoFocus style={{ maxHeight: '100%' }}>
          <div className='relative w-full flex flex-col px-5 py-3 gap-3 overflow-x-hidden overflow-y-auto'>
            <div className='relative w-full flex flex-col pt-1.5 gap-1.5 items-center justify-center text-center'>
              <h2 tabIndex={0} className='font-semibold'>
                {termsAndPrivacyDetails &&
                  `${
                    termsAndPrivacyDetails?.brandLegalName ||
                    termsAndPrivacyDetails?.brandName
                  } x `}
                {t('brijLegalName')}
              </h2>
              <span tabIndex={0} className='text-xxs text-[#636369]'>
                {t('subtitle')}
              </span>
            </div>
            <p
              tabIndex={0}
              className='w-full py-1.5 text-left text-xxs leading-[15px] text-black'
            >
              {t('legalNotice').replaceAll(
                '{{brand_name}}',
                termsAndPrivacyDetails
                  ? termsAndPrivacyDetails?.brandLegalName ||
                      termsAndPrivacyDetails?.brandName
                  : 'brands'
              )}
              {location.pathname === '/p/BRIJ'
                ? t('legalNoticeBrij')
                : t('legalNoticeMixed').replaceAll(
                    '{{brand_name}}',
                    termsAndPrivacyDetails
                      ? termsAndPrivacyDetails?.brandLegalName ||
                          termsAndPrivacyDetails?.brandName
                      : 'brands'
                  )}
            </p>
            {termsAndPrivacyDetails &&
              location.pathname !== '/p/BRIJ' &&
              termsAndPrivacyDetails?.termsAndConditionsURL && (
                <Button
                  variant='dark'
                  aria-label={t('ariaBrandTerms')}
                  title={`${
                    termsAndPrivacyDetails?.brandLegalName ||
                    termsAndPrivacyDetails?.brandName
                  } - ${t('termsButton')}`}
                  styles='!h-termsButton'
                  textStyles='sm:!leading-[18px] md:!leading-5'
                  onClick={() =>
                    redirectToExternalLink(
                      termsAndPrivacyDetails?.termsAndConditionsURL,
                      '_blank'
                    )
                  }
                />
              )}
            {termsAndPrivacyDetails &&
              location.pathname !== '/p/BRIJ' &&
              termsAndPrivacyDetails?.privacyPolicyURL && (
                <Button
                  variant='dark'
                  aria-label={t('ariaBrandPrivacy')}
                  title={`${
                    termsAndPrivacyDetails?.brandLegalName ||
                    termsAndPrivacyDetails.brandName
                  } - ${t('privacyButton')}`}
                  styles='!h-termsButton'
                  textStyles='sm:!leading-[18px] md:!leading-5'
                  onClick={() =>
                    redirectToExternalLink(
                      termsAndPrivacyDetails.privacyPolicyURL,
                      '_blank'
                    )
                  }
                />
              )}
            <Button
              variant='dark'
              aria-label={t('ariaTerms')}
              title={`Brij Inc. - ${t('termsButton')}`}
              styles='!h-termsButton'
              textStyles='sm:!leading-[18px] md:!leading-5'
              onClick={() =>
                redirectToExternalLink('https://brij.it/terms', '_blank')
              }
            />
            <Button
              variant='dark'
              styles='!h-termsButton'
              aria-label={t('ariaPrivacy')}
              title={`Brij Inc. - ${t('privacyButton')}`}
              textStyles='sm:!leading-[18px] md:!leading-5'
              onClick={() =>
                redirectToExternalLink(
                  'https://brij.it/privacy-center',
                  '_blank'
                )
              }
            />
            {productDetails?.brand?.cookieConsentSettings &&
              cookiePreferences && (
                <div
                  tabIndex={0}
                  aria-expanded={cookieDropdownOpen}
                  style={{
                    border: `1px solid ${shadeChanger(brandTheme, 50)}`,
                    background: `${hex2rgba(brandTheme, 0.05)}`,
                  }}
                  aria-label={`Cookie preferences, ${
                    cookieDropdownOpen ? 'expanded' : 'collapsed'
                  }`}
                  className={`
                    w-full min-h-[3.25rem] relative flex flex-col items-start justify-center transition-all duration-300 
                    ${getCornerStyle()} 
                  `}
                >
                  <button
                    type='button'
                    className='relative w-full flex flex-row items-center justify-center px-4 py-3.5'
                    onClick={() => setCookieDropdownOpen(!cookieDropdownOpen)}
                  >
                    <label className='text-center text-primary font-bold pointer-events-none'>
                      Cookie Preferences
                    </label>
                    <Chevron
                      fill={brandTheme}
                      className={`
                      absolute right-3 transform duration-150 
                      ${cookieDropdownOpen ? 'rotate-180' : 'rotate-0'}
                    `}
                    />
                  </button>
                  {cookieDropdownOpen && (
                    <div className='relative w-full flex flex-col items-start justify-start text-left px-4 pt-0.5 pb-3'>
                      <h6
                        tabIndex={1}
                        aria-label='Accept the use of Cookies'
                        className='font-semibold text-black'
                      >
                        {cookieBanner('bannerHeading')}
                      </h6>
                      <HtmlWrapper
                        styles='!mt-0.5 !text-black !text-xs'
                        html={
                          productDetails?.brand?.cookieConsentSettings
                            ?.description || ''
                        }
                        linkTrackingParams={
                          productDetails?.product?.linkTrackingParams
                        }
                        experienceData={experienceData}
                      />
                      <div className='relative w-full flex flex-col items-start justify-start text-left px-0.5 py-4 gap-2.5'>
                        {Object.keys(cookiePreferences).map((key: string) => (
                          <button
                            key={key}
                            tabIndex={1}
                            type='button'
                            disabled={key === 'essential'}
                            onClick={() =>
                              handleCheckboxChange(
                                key as keyof CookiesConsentType
                              )
                            }
                            className={`w-full flex items-center gap-2 text-black ${
                              key === 'essential' && 'pointer-events-none'
                            }`}
                            aria-label={
                              cookiePreferences[key as keyof CookiesConsentType]
                                ? `${t('checkedStatus')}: ${getAriaLabel(
                                    key as keyof CookiesConsentType
                                  )}`
                                : `${t('uncheckedStatus')}: ${getAriaLabel(
                                    key as keyof CookiesConsentType
                                  )}`
                            }
                          >
                            <label
                              className={`
                              flex-shrink-0 w-5 h-5 min-w-5 min-h-5 flex items-center justify-center rounded border-solid focus:ring-black cursor-pointer 
                              ${
                                cookiePreferences[
                                  key as keyof CookiesConsentType
                                ] === true
                                  ? 'border-transparent'
                                  : 'border-gray-300'
                              }
                            `}
                            >
                              {cookiePreferences[
                                key as keyof CookiesConsentType
                              ] === true ? (
                                <img
                                  aria-hidden
                                  alt='checkbox'
                                  src={cookieCheckbox}
                                  className={`w-5 h-5 min-w-5 min-h-5 pointer-events-none ${
                                    key === 'essential' && 'opacity-60'
                                  }`}
                                />
                              ) : (
                                <div
                                  aria-hidden
                                  className='!w-[17px] !h-[17px] !min-w-[17px] !min-h-[17px] pointer-events-none'
                                />
                              )}
                            </label>
                            <span
                              className={`text-left text-xs text-black cursor-pointer ${
                                key === 'essential' && 'opacity-60'
                              }`}
                            >
                              {key === 'essential' &&
                                cookieBanner('essentialCookiesDescription')}
                              {key === 'functional' &&
                                cookieBanner('functionalCookiesDescription')}
                              {key === 'analytics' &&
                                cookieBanner('analyticsCookiesDescription')}
                              {key === 'marketing' &&
                                cookieBanner('marketingCookiesDescription')}
                              {key === 'social' &&
                                cookieBanner('socialCookiesDescription')}
                            </span>
                          </button>
                        ))}
                      </div>
                      <Button
                        variant='dark'
                        loading={loading}
                        styles='!h-termsButton !mx-auto'
                        onClick={() => handleAcceptSelected()}
                        title={cookieBanner('savePreferences')}
                        aria-label={cookieBanner('savePreferences')}
                        textStyles='sm:!leading-[18px] md:!leading-5'
                      />
                    </div>
                  )}
                </div>
              )}
            <p tabIndex={0} className='text-xs text-center font-medium mb-4'>
              {t('questionsAboutData')}{' '}
              <a
                className='text-primary underline focus-visible:outline focus-visible:outline-black/60 focus-visible:outline-offset-2'
                href={`mailto:support@brij.it`}
              >
                {t('contactUs')}
              </a>
            </p>
          </div>
        </SimpleBar>
      )}
    </PageWrapper>
  );
};

export default TermsAndPrivacy;
